import { forwardRef, memo, useState } from "react";
import clsx from "clsx";
import { useBreakLG } from "../../utils";
import { Arrow3 } from "@monta/icons";

import type Props from "./types";

const variantClasses: Record<NonNullable<Props["variant"]>, string> = {
  elevated: "shadow-sm border-transparent",
  outlined: "border !border-[#E2E8F0]",
  ghost: "border-transparent"
};

const sizeClasses: Record<NonNullable<Props["size"]>, string> = {
  sm: "p-3 lg:py-4 lg:px-5",
  md: "p-3 lg:py-6 lg:px-8",
  lg: "p-4 lg:py-8 lg:px-10"
};

const ToolCard = memo(
  forwardRef<HTMLDivElement, Readonly<Props>>(function ToolCard(
    {
      as: Component = "section",
      title,
      tooltip = true,
      icon,
      actions,
      children,
      variant = "outlined",
      size = "md",
      className = "",
      headerProps,
      bodyProps,
      id,
      ariaLabel,
      collapsible = false,
      defaultOpen = false
    },
    ref
  ) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const isDesktop = useBreakLG();

    const getHeaderClasses = () => {
      const baseClasses =
        "flex items-center justify-between w-full h-[34px] lg:pb-0  focus-visible:border-none focus-visible:outline-none";

      const conditionalClasses = {
        "cursor-pointer": !isDesktop && collapsible,
        "mb-3 pb-3": isOpen && !isDesktop,
        "mb-0 pb-0 !border-b-[0]": collapsible && !isOpen && !isDesktop
      };

      return clsx(baseClasses, conditionalClasses, headerProps?.className);
    };

    const getBodyClasses = () => {
      const baseClasses = "w-full";

      const conditionalClasses = {
        hidden: !isDesktop && collapsible && !isOpen
      };

      return clsx(baseClasses, conditionalClasses, bodyProps?.className);
    };

    const getArrowClasses = () => {
      const baseClasses = " transition-transform duration-200";

      const conditionalClasses = {
        "rotate-0": isOpen,
        "rotate-180": !isOpen
      };

      return clsx(baseClasses, conditionalClasses);
    };

    return (
      <Component
        ref={ref}
        id={id}
        aria-label={ariaLabel}
        className={clsx(
          "w-full bg-white rounded-2",
          sizeClasses[size],
          variantClasses[variant],
          className
        )}
      >
        {(title || tooltip || icon || actions) && (
          <div
            {...headerProps}
            onClick={
              !isDesktop && collapsible ? () => setIsOpen(!isOpen) : undefined
            }
            onKeyDown={
              !isDesktop && collapsible
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setIsOpen(!isOpen);
                    }
                  }
                : undefined
            }
            tabIndex={!isDesktop && collapsible ? 0 : undefined}
            role={!isDesktop && collapsible ? "button" : undefined}
            className={getHeaderClasses()}
          >
            <div className="flex items-center">
              {icon}

              {title && (
                <h2 className="lg:text-normal text-sm print:text-normal font-bold text-gray-800 leading-8">
                  {title}
                </h2>
              )}

              {tooltip && (
                <div className="rounded-[40%] flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors mr-3 print:!hidden">
                  {/* If tooltip is boolean true, show default icon; if node, render node */}
                  {tooltip}
                </div>
              )}
            </div>

            {actions}

            {!isDesktop && collapsible && (
              <Arrow3 className={getArrowClasses()} size={20} color="#1D2939" />
            )}
          </div>
        )}

        <div {...bodyProps} className={getBodyClasses()}>
          {children}
        </div>
      </Component>
    );
  })
);

export default ToolCard;
